import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  Input,
  Button,
} from '@you/ui'
import Icon from '@/components/Icon/Icon'
import { KnowledgeDocType, KnowledgeBase } from '@/api/types/knowledge.types'
import { useKnowledge } from '@/api/hooks/useKnowledge'
import { useWorkspaceStore } from '@/stores/workspaceStore'

interface UpdateKnowledgeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
  knowledge?: Partial<Pick<KnowledgeBase, 'id' | 'name' | 'docType'>>
}

const docTypeOptions = [
  { type: KnowledgeDocType.TEXT, icon: 'text', label: '文档' },
  { type: KnowledgeDocType.TABLE, icon: 'table', label: '表格' },
]

const UpdateKnowledgeDialog: React.FC<UpdateKnowledgeDialogProps> = ({
  open,
  onOpenChange,
  onSuccess,
  knowledge,
}) => {
  const [docType, setDocType] = useState<KnowledgeDocType>(
    KnowledgeDocType.TEXT
  )
  const [name, setName] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const { currentWorkspace } = useWorkspaceStore()
  const { updateKnowledge } = useKnowledge({
    workspaceId: currentWorkspace?.id || '',
  })

  // 初始化编辑数据
  useEffect(() => {
    if (open) {
      setDocType(knowledge?.docType ?? KnowledgeDocType.TEXT)
      setName(knowledge?.name ?? '')
      setError('')
      setLoading(false)
    }
  }, [open, knowledge])

  const handleSave = async () => {
    if (!name.trim()) {
      setError('知识库名称不能为空')
      return
    }
    setError('')
    setLoading(true)
    try {
      // 构建请求参数，如果没有id则不传递该字段
      const params: any = {
        name: name.trim(),
        docType,
        workspaceId: currentWorkspace?.id || '',
      }
      
      // 只有在有id的情况下才添加id字段
      if (knowledge?.id) {
        params.id = knowledge.id
      }
      
      await updateKnowledge(params)

      setLoading(false)
      onOpenChange(false)
      setName('')
      if (onSuccess) onSuccess()
    } catch (e: unknown) {
      setLoading(false)
      if (e instanceof Error) {
        setError(e.message || '保存失败')
      } else {
        setError('保存失败')
      }
    }
  }

  const handleClose = () => {
    setName('')
    setError('')
    setLoading(false)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[580px]">
        <DialogHeader title={knowledge?.id ? '编辑知识库' : '新建知识库'} />
        <div className="flex flex-col items-center px-6">
          <div className="flex justify-start gap-6 my-4 w-full">
            {docTypeOptions.map((opt) => (
              <button
                key={opt.type}
                type="button"
                className={`flex flex-col items-center px-4 w-[160px] py-2 rounded border transition-all ${
                  docType === opt.type
                    ? 'border-[#8752FF] bg-[#F3EEFF]'
                    : 'border-transparent bg-transparent'
                } hover:border-[#8752FF]`}
                onClick={() => setDocType(opt.type)}
              >
                <Icon
                  name={opt.icon}
                  size={40}
                  className={
                    docType === opt.type ? 'text-[#8752FF]' : 'text-gray-400'
                  }
                />
                <span
                  className={`mt-1 text-xs ${
                    docType === opt.type ? 'text-[#8752FF]' : 'text-gray-500'
                  }`}
                >
                  {opt.label}
                </span>
              </button>
            ))}
          </div>
          <div className="mb-2 text-left w-full">
            <label className="mb-1 text-base font-normal flex items-center">
              <span className="text-red-500 mr-1">*</span>名称
            </label>
            <Input
              error={!!error}
              errorMessage={error}
              placeholder="请输入知识库名称"
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                if (error) {
                  setError('')
                }
              }}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            取消
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            保存
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default UpdateKnowledgeDialog
